// ============================================================================
// statusConfig.js — CANONICAL STATUS VISUAL/LABEL SOURCE OF TRUTH (Phase 2)
//
// One place that maps ReferralGate Gate outcomes, workflow statuses, priorities,
// rework statuses, and generic alert kinds to their LABEL + VISUAL SEMANTICS.
// Components must not redefine status color maps locally — they call the getters
// here (and render via RGStatusBadge).
//
// IMPORTANT:
//   • Enum VALUES are imported from store.workflow.js / store.rework.js — this
//     file adds visual/label semantics ONLY. It does not define workflow states,
//     transitions, or outcome logic, and performs no Firestore I/O.
//   • NO hex colors here. All classes are token-backed Tailwind utilities
//     (bg-status-*, text-status-*, border-status-*) whose values live in
//     src/styles/tokens.css and are registered in tailwind.config.js.
//   • Domain invariants preserved:
//       URGENT = red/danger · REDIRECT = indigo/routing (NOT urgent)
//       NOT_YET = amber (incomplete Gate) · NEEDS_INFO = orange (operational rework)
//       CLOSED_LOOP = teal/terminal completed
//   • Unknown values never throw — they fall back to a neutral badge.
// ============================================================================

import { WORKFLOW_STATUS, WORKFLOW_PRIORITY } from './store.workflow.js';
import { REWORK_REQUEST_STATUS } from './store.rework.js';

// ── Token → className helper ────────────────────────────────────────────────
// token is a registered status key (see tailwind.config.js statusKeys).
function classes(token) {
  return {
    badgeClassName: `bg-status-${token} text-status-${token} border-status-${token}`,
    bgClassName: `bg-status-${token}`,
    textClassName: `text-status-${token}`,
    borderClassName: `border-status-${token}`,
    dotClassName: `bg-status-${token}`,
  };
}

function meta({ key, label, description, semanticType, token, iconName }) {
  return { key, label, description, semanticType, iconName, ...classes(token) };
}

// ── Public status-key catalogs (labels/visuals only; values come from enums) ──
export const OUTCOME_STATUS = {
  URGENT:  'urgent',
  ROUTINE: 'routine',
  NOT_YET: 'not_yet',
  REDIRECT:'redirect',
};

export const WORKFLOW_STATUS_KEYS = WORKFLOW_STATUS;   // re-export for convenience
export const PRIORITY_STATUS = WORKFLOW_PRIORITY;      // { NORMAL, HIGH, URGENT }
export const REWORK_STATUS = REWORK_REQUEST_STATUS;    // { OPEN, RECEIVED, RESOLVED, CANCELLED }

export const GENERIC_STATUS = {
  WARNING: 'warning',
  DANGER:  'danger',
  SUCCESS: 'success',
  INFO:    'info',
  ON_HOLD: 'on_hold',
};

// Umbrella for getStatusMeta(value, { type })
export const STATUS = {
  OUTCOME:  'outcome',
  WORKFLOW: 'workflow',
  PRIORITY: 'priority',
  GENERIC:  'generic',
  REWORK:   'rework',
};

// ── Meta tables ──────────────────────────────────────────────────────────────

const OUTCOME_META = {
  urgent:   meta({ key: 'urgent',   label: 'Urgent',   description: 'Patient risk / faster review required',        semanticType: 'danger',  token: 'urgent',   iconName: 'AlertTriangle' }),
  routine:  meta({ key: 'routine',  label: 'Routine',  description: 'Accepted / normal pathway',                    semanticType: 'success', token: 'routine',  iconName: 'CheckCircle' }),
  not_yet:  meta({ key: 'not_yet',  label: 'Not Yet',  description: 'Incomplete Gate criteria / recoverable input', semanticType: 'warning', token: 'not-yet',  iconName: 'Clock' }),
  redirect: meta({ key: 'redirect', label: 'Redirect', description: 'Routing / ownership change, not urgency',      semanticType: 'info',    token: 'redirect', iconName: 'Share2' }),
};

const WORKFLOW_META = {
  [WORKFLOW_STATUS.NEW]:               meta({ key: WORKFLOW_STATUS.NEW,               label: 'New',               description: 'Newly submitted, awaiting first review', semanticType: 'info',     token: 'new',         iconName: 'Inbox' }),
  [WORKFLOW_STATUS.NEEDS_INFO]:        meta({ key: WORKFLOW_STATUS.NEEDS_INFO,        label: 'Needs Info',        description: 'Operational rework / action needed',     semanticType: 'warning',  token: 'needs-info',  iconName: 'AlertCircle' }),
  [WORKFLOW_STATUS.READY_TO_SCHEDULE]: meta({ key: WORKFLOW_STATUS.READY_TO_SCHEDULE, label: 'Ready to Schedule', description: 'Scheduling-ready work',                  semanticType: 'ready',    token: 'ready',       iconName: 'CalendarClock' }),
  [WORKFLOW_STATUS.SCHEDULED]:         meta({ key: WORKFLOW_STATUS.SCHEDULED,         label: 'Scheduled',         description: 'Appointment booked',                     semanticType: 'info',     token: 'scheduled',   iconName: 'Calendar' }),
  [WORKFLOW_STATUS.SEEN]:              meta({ key: WORKFLOW_STATUS.SEEN,              label: 'Seen',              description: 'Patient seen, awaiting closure',         semanticType: 'info',     token: 'seen',        iconName: 'Eye' }),
  [WORKFLOW_STATUS.CLOSED_LOOP]:       meta({ key: WORKFLOW_STATUS.CLOSED_LOOP,       label: 'Closed Loop',       description: 'Terminal / completed form',              semanticType: 'success',  token: 'closed-loop', iconName: 'CheckCircle2' }),
  [WORKFLOW_STATUS.CANCELLED]:         meta({ key: WORKFLOW_STATUS.CANCELLED,         label: 'Cancelled',         description: 'Terminal — no care delivered',           semanticType: 'danger',   token: 'danger',      iconName: 'XCircle' }),
  [WORKFLOW_STATUS.NO_SHOW]:           meta({ key: WORKFLOW_STATUS.NO_SHOW,           label: 'No-Show',           description: 'Appointment did not complete',           semanticType: 'danger',   token: 'no-show',     iconName: 'UserX' }),
};

const PRIORITY_META = {
  [WORKFLOW_PRIORITY.URGENT]: meta({ key: WORKFLOW_PRIORITY.URGENT, label: 'Urgent', description: 'Highest priority', semanticType: 'danger',  token: 'urgent',     iconName: 'AlertTriangle' }),
  [WORKFLOW_PRIORITY.HIGH]:   meta({ key: WORKFLOW_PRIORITY.HIGH,   label: 'High',   description: 'Elevated priority', semanticType: 'warning', token: 'needs-info', iconName: 'ArrowUp' }),
  [WORKFLOW_PRIORITY.NORMAL]: meta({ key: WORKFLOW_PRIORITY.NORMAL, label: 'Normal', description: 'Standard priority', semanticType: 'neutral', token: 'neutral',    iconName: null }),
  low:                        meta({ key: 'low',                    label: 'Low',    description: 'Low priority',      semanticType: 'neutral', token: 'neutral',    iconName: null }),
};

const GENERIC_META = {
  warning: meta({ key: 'warning', label: 'Warning', description: '', semanticType: 'warning', token: 'warning', iconName: 'AlertTriangle' }),
  danger:  meta({ key: 'danger',  label: 'Danger',  description: '', semanticType: 'danger',  token: 'danger',  iconName: 'AlertTriangle' }),
  success: meta({ key: 'success', label: 'Success', description: '', semanticType: 'success', token: 'success', iconName: 'CheckCircle' }),
  info:    meta({ key: 'info',    label: 'Info',    description: '', semanticType: 'info',    token: 'info',    iconName: 'Info' }),
  on_hold: meta({ key: 'on_hold', label: 'On Hold', description: 'Temporarily paused exception', semanticType: 'neutral', token: 'on-hold', iconName: 'PauseCircle' }),
};

const REWORK_META = {
  [REWORK_REQUEST_STATUS.OPEN]:      meta({ key: REWORK_REQUEST_STATUS.OPEN,      label: 'Open',      description: 'Awaiting missing information', semanticType: 'warning', token: 'warning', iconName: 'Clock' }),
  [REWORK_REQUEST_STATUS.RECEIVED]:  meta({ key: REWORK_REQUEST_STATUS.RECEIVED,  label: 'Received',  description: 'Missing information received',  semanticType: 'success', token: 'success', iconName: 'CheckCircle' }),
  [REWORK_REQUEST_STATUS.RESOLVED]:  meta({ key: REWORK_REQUEST_STATUS.RESOLVED,  label: 'Resolved',  description: 'Rework resolved',              semanticType: 'success', token: 'success', iconName: 'CheckCircle' }),
  [REWORK_REQUEST_STATUS.CANCELLED]: meta({ key: REWORK_REQUEST_STATUS.CANCELLED, label: 'Cancelled', description: 'Rework cancelled',              semanticType: 'neutral', token: 'neutral', iconName: 'XCircle' }),
};

// ── Helpers ────────────────────────────────────────────────────────────────

export function normalizeStatus(value) {
  if (value == null) return '';
  return String(value).trim();
}

function humanize(value) {
  const v = normalizeStatus(value);
  if (!v) return '';
  return v.toLowerCase().replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function unknownMeta(value) {
  return {
    key: 'unknown',
    label: humanize(value) || 'Unknown',
    description: '',
    semanticType: 'unknown',
    iconName: null,
    ...classes('neutral'),
  };
}

// ── Public getters (tolerant of case; never throw) ────────────────────────────

export function getOutcomeMeta(outcome) {
  const k = normalizeStatus(outcome).toLowerCase();
  return OUTCOME_META[k] || unknownMeta(outcome);
}

export function getWorkflowStatusMeta(status) {
  const k = normalizeStatus(status).toUpperCase();
  return WORKFLOW_META[k] || unknownMeta(status);
}

export function getPriorityMeta(priority) {
  const k = normalizeStatus(priority).toLowerCase();
  return PRIORITY_META[k] || unknownMeta(priority);
}

export function getGenericStatusMeta(kind) {
  const k = normalizeStatus(kind).toLowerCase();
  return GENERIC_META[k] || unknownMeta(kind);
}

export function getReworkStatusMeta(status) {
  const k = normalizeStatus(status).toLowerCase();
  return REWORK_META[k] || unknownMeta(status);
}

/** Generic dispatcher used by RGStatusBadge. */
export function getStatusMeta(value, { type = STATUS.GENERIC } = {}) {
  switch (type) {
    case STATUS.OUTCOME:  return getOutcomeMeta(value);
    case STATUS.WORKFLOW: return getWorkflowStatusMeta(value);
    case STATUS.PRIORITY: return getPriorityMeta(value);
    case STATUS.REWORK:   return getReworkStatusMeta(value);
    case STATUS.GENERIC:
    default:              return getGenericStatusMeta(value);
  }
}
